import React from 'react';
import type { UnitResponse } from '@troia-v3/types';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { CardActionsMenu } from '@/shared/components/ui/CardActionsMenu';
import type { CardAction } from '@/shared/components/ui/CardActionsMenu';
import { StatusPill, Switch } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { formatDocumentInput } from '@/shared/utils/documentMask';

/**
 * Card de unidade (view "Cards" + fallback mobile). Padrão-lista travado:
 * card flat/borda SEM checkbox (F34 — seleção em massa só na tabela) + menu
 * ••• de ações + switch inline de status. Avatar sem foto = slate.
 */
export interface UnitCardProps {
  unit: UnitResponse;
  onEdit?: (unit: UnitResponse) => void;
  onDelete?: (unit: UnitResponse) => void;
  onToggleStatus?: (unit: UnitResponse) => void;
}

const formatLocation = (unit: UnitResponse): string | null => {
  const city = unit.address?.city?.trim();
  const state = unit.address?.state?.trim();
  if (city && state) return `${city}, ${state}`;
  if (city) return city;
  return null;
};

export const UnitCard: React.FC<UnitCardProps> = ({ unit, onEdit, onDelete, onToggleStatus }) => {
  const isActive = unit.status === 'active';
  const location = formatLocation(unit);

  const actions: CardAction[] = [];
  if (onEdit) actions.push({ label: 'Editar', icon: 'edit', onClick: () => onEdit(unit) });
  if (onToggleStatus) {
    actions.push({
      label: isActive ? 'Desativar' : 'Ativar',
      icon: 'power',
      onClick: () => onToggleStatus(unit),
    });
  }
  if (onDelete) actions.push({ label: 'Excluir', icon: 'trash', onClick: () => onDelete(unit), variant: 'danger', dividerBefore: true });

  return (
    <div
      className="group relative rounded-xl border p-3.5 transition-colors"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
      data-testid={`unit-card-${unit.id}`}
    >
      {/* Header: avatar + nome/código + ações */}
      <div className="flex items-start gap-3">
        <Avatar className="w-11 h-11 flex-shrink-0" isSquircle>
          <AvatarFallback isSquircle>
            <Icon name="map-pin" className="w-5 h-5 text-white" />
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-sm truncate" style={{ color: 'var(--theme-card-text)' }}>
            {unit.name}
          </h3>
          <p className="text-xs truncate" style={{ color: 'var(--theme-muted-text)' }}>
            {unit.code || 'Sem código'}
          </p>
          {unit.document && (
            <p
              className="text-xs truncate"
              style={{ color: 'var(--theme-muted-text)' }}
              data-testid={`unit-card-document-${unit.id}`}
            >
              {formatDocumentInput(unit.document, 'PJ')}
            </p>
          )}
        </div>

        {actions.length > 0 && (
          <div className="flex-shrink-0 -mr-1 -mt-1">
            <CardActionsMenu actions={actions} size="sm" data-testid={`unit-card-actions-${unit.id}`} />
          </div>
        )}
      </div>

      {/* Status */}
      <div className="mt-3">
        {onToggleStatus ? (
          <div className="inline-flex items-center gap-2" data-testid={`unit-card-toggle-${unit.id}`}>
            <Switch size="sm" checked={isActive} onChange={() => onToggleStatus(unit)} />
            <span className="text-xs font-semibold" style={{ color: isActive ? 'var(--theme-success-fg)' : 'var(--theme-muted-text)' }}>
              {isActive ? 'Ativa' : 'Inativa'}
            </span>
          </div>
        ) : (
          <StatusPill
            variant={isActive ? 'success' : 'neutral'}
            label={isActive ? 'Ativa' : 'Inativa'}
            data-testid={`unit-card-status-${unit.id}`}
          />
        )}
      </div>

      {/* Endereço (cidade/UF) */}
      <div className="mt-3 pt-3 flex items-center gap-1.5 text-xs" style={{ borderTop: '1px solid var(--theme-card-border)', color: 'var(--theme-muted-text)' }}>
        <Icon name="map-pin" className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="truncate">{location ?? 'Endereço não informado'}</span>
      </div>
    </div>
  );
};

UnitCard.displayName = 'UnitCard';
